import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { SignalProgress } from "@/components/ui/signal-progress";
import { useNavigate } from "react-router-dom";
import { MapPin, Edit, User, Loader2, Mail, Eye, EyeOff } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useProfileInterests } from "@/hooks/useInterests";
import { useStudentSkills } from "@/hooks/useStudentSkills";
import { getSkillById } from "@/lib/skills";

export default function AlunoPerfil() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile();
  const { interests } = useProfileInterests();
  const { skills } = useStudentSkills();

  const isLoading = authLoading || profileLoading;

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container text-center py-20">
        <p className="text-muted-foreground">Faça login para ver seu perfil.</p>
        <Button variant="hero" className="mt-4" onClick={() => navigate("/aluno/login")}>Entrar</Button>
      </div>
    );
  }

  const hasBio = !!profile.bio;
  const hasObjetivo = !!profile.objective;
  const hasInteresses = interests.length > 0;
  const hasSkills = skills.length > 0;

  const signals = [hasBio, hasObjetivo, hasInteresses, hasSkills].filter(Boolean).length;
  const totalSignals = 6;

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground">Meu Perfil</h1>
        <Button variant="outline" size="sm" onClick={() => navigate("/aluno/perfil/editar")}>
          <Edit className="w-4 h-4" />Editar
        </Button>
      </motion.div>

      {/* Profile Card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-5 shadow-card border border-border mb-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <User className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold text-foreground">{profile.full_name || "Sem nome"}</h2>
            
            {/* Email */}
            <div className="flex items-center gap-1 text-muted-foreground text-sm mt-1">
              <Mail className="w-4 h-4" />
              <span>{user?.email ?? "—"}</span>
            </div>

            {/* Location */}
            {(profile.city || profile.state) && (
              <div className="flex items-center gap-1 text-muted-foreground text-sm mt-1">
                <MapPin className="w-4 h-4" />
                <span>{[profile.city, profile.state].filter(Boolean).join(", ")}</span>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <StatusBadge status={profile.profile_status as "construction" | "evolution" | "ready"} />
              {profile.visibility_global ? (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Eye className="w-3 h-3" /> Visível
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
                  <EyeOff className="w-3 h-3" /> Oculto
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Sinais de Carreira</span>
            <SignalProgress current={signals} total={totalSignals} />
          </div>
        </div>
      </motion.div>

      {/* Bio */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
        <h3 className="font-heading text-base font-semibold text-foreground mb-3">Bio</h3>
        {hasBio ? (
          <p className="text-muted-foreground text-sm bg-card rounded-xl p-4 border border-border">{profile.bio}</p>
        ) : (
          <div className="bg-accent/10 rounded-xl p-4 border border-accent/20">
            <p className="text-sm text-muted-foreground mb-2">Adicione uma breve descrição sobre você</p>
            <Button size="sm" variant="accent" onClick={() => navigate("/aluno/perfil/editar")}>Adicionar Bio</Button>
          </div>
        )}
      </motion.section>

      {/* Objetivo */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-6">
        <h3 className="font-heading text-base font-semibold text-foreground mb-3">Objetivo Atual</h3>
        {hasObjetivo ? (
          <Badge variant="default" className="text-sm">{profile.objective}</Badge>
        ) : (
          <div className="bg-accent/10 rounded-xl p-4 border border-accent/20">
            <p className="text-sm text-muted-foreground mb-2">O que você está buscando agora?</p>
            <Button size="sm" variant="accent" onClick={() => navigate("/aluno/perfil/editar")}>Definir Objetivo</Button>
          </div>
        )}
      </motion.section>

      {/* Interesses */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
        <h3 className="font-heading text-base font-semibold text-foreground mb-3">Interesses</h3>
        {hasInteresses ? (
          <div className="flex flex-wrap gap-2">
            {interests.map((item) => (
              <Badge key={item.tag_id} variant="secondary">
                {(item as any).interest_tags?.name ?? item.tag_id}
              </Badge>
            ))}
          </div>
        ) : (
          <div className="bg-accent/10 rounded-xl p-4 border border-accent/20">
            <p className="text-sm text-muted-foreground mb-2">Quais áreas te interessam?</p>
            <Button size="sm" variant="accent" onClick={() => navigate("/aluno/perfil/editar")}>Adicionar Interesses</Button>
          </div>
        )}
      </motion.section>

      {/* Habilidades */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mb-6">
        <h3 className="font-heading text-base font-semibold text-foreground mb-3">Habilidades</h3>
        {hasSkills ? (
          <div className="flex flex-wrap gap-2">
            {skills.map((skillId) => {
              const skill = getSkillById(skillId);
              return (
                <Badge key={skillId} variant="default" className="text-xs">
                  {skill?.label ?? skillId}
                </Badge>
              );
            })}
          </div>
        ) : (
          <div className="bg-accent/10 rounded-xl p-4 border border-accent/20">
            <p className="text-sm text-muted-foreground mb-2">Quais são suas habilidades?</p>
            <Button size="sm" variant="accent" onClick={() => navigate("/aluno/perfil/editar")}>Adicionar Habilidades</Button>
          </div>
        )}
      </motion.section>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="bg-primary/5 rounded-2xl p-4 border border-primary/10">
        <p className="text-sm text-center text-foreground font-medium">
          Este perfil representa uma trajetória, não um currículo perfeito.
        </p>
      </motion.div>
    </div>
  );
}
